"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ArrowUp, Twitter, Send, Radio, Activity, LucideIcon } from "lucide-react";
interface SocialLink {
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  url: string;
}
interface SystemStatus {
  label: string;
  status: string;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3].map((_, i) => <li key={i} data-index={i}>
            <div className="h-3 w-24 bg-muted animate-pulse" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <div className="text-muted-foreground font-mono">NO LINKS FOUND</div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors uppercase tracking-widest flex items-center gap-2">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  const currentYear = new Date().getFullYear();
  const socialLinks: SocialLink[] = [{
    name: "SPOTIFY",
    icon: Radio,
    url: "#"
  }, {
    name: "INSTAGRAM",
    icon: Twitter,
    url: "https://www.instagram.com/iammileshen.music/"
  }, {
    name: "LINKEDIN",
    icon: Send,
    url: "https://www.linkedin.com/in/miles-henshaw-90218b153"
  }];
  const systemStatus: SystemStatus[] = [{
    label: "AVAILABILITY",
    status: "BOOKING"
  }, {
    label: "LOCATION",
    status: "UK_BASE"
  }, {
    label: "SIGNAL",
    status: "STABLE"
  }];
  return <section data-section-id="227295" id="footer" className="bg-background text-foreground py-16 px-6 font-mono text-xs border-t border-border/20 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:100%_2px] opacity-30" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="flex flex-col gap-6">
            <Link to="/" className="inline-block w-fit">
              
            </Link>
            <p className="text-muted-foreground leading-relaxed pr-4 font-sans text-sm">Miles Henshaw is a music producer and mix engineer based in Hull. Specialising in alternative and experimental sound.</p>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-foreground font-default font-semibold tracking-widest uppercase flex items-center gap-2 text-sm">
              <Activity className="size-3 text-primary" />
              Navigation
            </h3>
            <MenuProvider menu_id="45">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-foreground font-default font-semibold tracking-widest uppercase flex items-center gap-2 text-sm">
              <Radio className="size-3 text-primary" />
              Socials
            </h3>
            <ul className="space-y-3">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <li key={social.name} data-index={i}>
                    <Link to={social.url} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-3 uppercase tracking-widest group font-mono text-[10px]">
                      <span className="p-1.5 border border-border/30 bg-muted/10 group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors">
                        <Icon className="size-3" />
                      </span>
                      {social.name}
                    </Link>
                  </li>;
            })}
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-foreground font-default font-semibold tracking-widest uppercase flex items-center gap-2 text-sm">
              <Activity className="size-3 text-primary" />
              Studio Status
            </h3>
            <ul className="space-y-3">
              {systemStatus.map((item, i) => <li key={item.label} data-index={i} className="flex items-center justify-between border-b border-border/10 pb-2">
                  <span className="text-muted-foreground uppercase tracking-widest font-mono text-[10px]">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-primary uppercase tracking-widest font-mono text-[10px]">{item.status}</span>
                    <div className="size-1 bg-primary animate-pulse" />
                  </div>
                </li>)}
            </ul>
          </div>
        </div>

        <Separator className="bg-border/20 mb-8" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-muted-foreground uppercase tracking-widest flex items-center gap-4 font-mono text-[10px]">
            <span>&copy; {currentYear} MILES HENSHAW.</span>
            <span className="hidden sm:inline-block text-border/30">|</span>
          </div>

          <Button variant="outline" size="sm" onClick={scrollToTop} className="font-mono text-[10px] border-border/30 bg-transparent hover:bg-primary/5 hover:text-primary hover:border-primary/50 uppercase tracking-widest transition-all duration-300">
            <ArrowUp className="size-3 mr-2" />
            Return to Top
          </Button>
        </div>
      </div>
    </section>;
}